
/* ============================== src/components/Modals/ProjectAccessModal.jsx ============================== */
// path: frontend/src/components/Modals/ProjectAccessModal.jsx
/**
 * Управление персональным доступом к проекту.
 *
 * lockedIds — сотрудники, которые видят проект «по роли» — приходят с
 * GET /projects/:id/access-by-role (сервер считает по effective-ролям,
 * scope, projectVisible). Раньше дублировалось на клиенте через
 * canSeeProjectByDefault — удалено; источник правды — бэкенд.
 *
 * Пока запрос не ответил, lockedIds пуст (все чекбоксы редактируемы, но
 * при сохранении PATCH /projects/:id отбросит попытку снять «доступ по
 * роли» — accessUserIds хранит только явный доступ).
 *
 * Запись — PATCH /projects/:id { accessUserIds } с If-Match: version
 * (useUpdateProjectMutation) + 409 STALE_VERSION handling.
 */
import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ModalShell } from "../ModalShell";
import Avatar from "../Avatar";
import { SearchBox } from "../SearchBox";
import { api } from "../../api/client";
import { useNestedModalEscape } from "../Templates/useNestedModalEscape";
import { useUpdateProjectMutation } from "../../api/queries/projects";
import { useConfirm } from "../../context/ConfirmContext";

export const ProjectAccessModal = ({ db, projectId, onClose, toast }) => {
  useNestedModalEscape(onClose);

  const project = db.projects.find((p) => p.id === projectId);
  const updateMutation = useUpdateProjectMutation();
  const { confirm } = useConfirm();

  const { data: lockedIdsData } = useQuery({
    queryKey: ["project", projectId, "access-by-role"],
    queryFn: () => api.get(`/projects/${projectId}/access-by-role`),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const [userIds, setUserIds] = useState(() => {
    const initial = project?.access?.userIds;
    return Array.isArray(initial) ? [...initial] : [];
  });
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  const activeEmployees = useMemo(
    () =>
      db.employees
        .filter((e) => !e.fired)
        .sort(
          (a, b) =>
            (a.last || "").localeCompare(b.last || "", "ru") ||
            (a.first || "").localeCompare(b.first || "", "ru"),
        ),
    [db.employees],
  );

  const lockedIds = useMemo(
    () => new Set(lockedIdsData?.lockedIds ?? []),
    [lockedIdsData],
  );

  const visibleEmployees = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activeEmployees;
    return activeEmployees.filter(
      (emp) =>
        `${emp.last} ${emp.first}`.toLowerCase().includes(q) ||
        (emp.position || "").toLowerCase().includes(q),
    );
  }, [activeEmployees, query]);

  const toggleUser = useCallback((id) => {
    setUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const save = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const prevIds = Array.isArray(project?.access?.userIds)
        ? project.access.userIds
        : [];
      const namesOf = (ids) =>
        ids
          .map((id) => db.employees.find((e) => e.id === id))
          .filter(Boolean)
          .map((e) => `${e.last} ${e.first}`);

      const addedNames = namesOf(userIds.filter((id) => !prevIds.includes(id)));
      const removedNames = namesOf(
        prevIds.filter((id) => !userIds.includes(id)),
      );

      const doMutate = (needsConfirmation) => {
        const args = {
          id: projectId,
          body: { accessUserIds: userIds },
          needsConfirmation,
        };
        if (!needsConfirmation) args.version = project?.version;
        return updateMutation.mutateAsync(args);
      };

      try {
        await doMutate(false);
      } catch (e) {
        if (e.status === 409 && e.details?.needsConfirmation) {
          const ok = await confirm({
            title: "Конфликт данных",
            message: e.message,
            confirmLabel: "Продолжить",
          });
          if (!ok) return;
          await doMutate(true);
        } else {
          throw e;
        }
      }

      const parts = [];
      if (addedNames.length) parts.push(`открыт для ${addedNames.join(", ")}`);
      if (removedNames.length)
        parts.push(`закрыт для ${removedNames.join(", ")}`);

      toast(
        parts.length
          ? `Доступ к проекту ${parts.join("; ")}`
          : "Доступ к проекту обновлён",
        "success",
      );
      onClose();
    } catch (error) {
      toast(error.message || "Не удалось обновить доступ", "error");
    } finally {
      setBusy(false);
    }
  }, [
    busy,
    projectId,
    userIds,
    project,
    db.employees,
    updateMutation,
    confirm,
    toast,
    onClose,
  ]);

  if (!project) return null;

  return (
    <ModalShell
      title={`Доступ к проекту: ${project.name}`}
      onClose={onClose}
      onSave={save}
      saveLabel={busy ? "Сохранение…" : "Сохранить"}
      width={560}
      className="modal-access"
    >
      <div className="access-section">
        <div className="access-section-head">
          <span className="access-section-title">Сотрудники</span>
          <span className="access-section-count">
            {userIds.length} с явным доступом
          </span>
        </div>

        <SearchBox
          value={query}
          onChange={setQuery}
          placeholder="Поиск по сотруднику или должности…"
          className="access-search"
        />

        <div className="access-list">
          {visibleEmployees.length === 0 ? (
            <div className="access-empty">Ничего не найдено</div>
          ) : (
            visibleEmployees.map((emp) => {
              const locked = lockedIds.has(emp.id);
              const checked = locked || userIds.includes(emp.id);
              return (
                <label key={emp.id} className="access-item">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={locked}
                    onChange={() => toggleUser(emp.id)}
                  />
                  <Avatar employee={emp} size="xs" />
                  <span className="access-item-name">
                    {emp.last} {emp.first}
                  </span>
                  <span className="access-item-note">
                    {locked ? "доступ по роли" : emp.position}
                  </span>
                </label>
              );
            })
          )}
        </div>
      </div>
    </ModalShell>
  );
};
